import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

/**
 * ZAgentWorkspaces — Zod schemas for the per-agent workspace-assignment
 * endpoints. Drives the "Workspaces" tab on the agent edit page.
 *
 * Routes:
 *   GET  /api/agents/:agentId/workspaces  — current assignments
 *   PUT  /api/agents/:agentId/workspaces  — replace the full assignment list
 *
 * Authorization: org-scoped via JWT + CurrentOrganizationId. The use case
 * verifies the agent belongs to the calling org before reading or writing,
 * surfacing 404 (not 403) when the agent is in another org so attackers
 * can't enumerate ids across orgs.
 */

const AgentWorkspaceModeSchema = z.enum(["READ", "READ_WRITE"]);

/** One workspace assignment, projected to the fields the UI needs to
 *  drive its checkbox + segmented control state. */
const SetAgentWorkspaceAssignmentSchema = z.object({
  workspaceId: z.string().uuid(),
  slug: z.string().min(1),
  mode: AgentWorkspaceModeSchema
});

const AgentWorkspaceAssignmentSchema = SetAgentWorkspaceAssignmentSchema.extend({
  isShared: z.boolean()
});

export const AgentWorkspacePathVarsSchema = z.object({
  agentId: z.string().min(1)
});

const AssignmentsResponseSchema = z.object({
  assignments: z.array(AgentWorkspaceAssignmentSchema)
});

export const SetAgentWorkspacesBodySchema = z.object({
  assignments: z
    .array(SetAgentWorkspaceAssignmentSchema)
    .refine((arr) => new Set(arr.map((a) => a.workspaceId)).size === arr.length, {
      message: "Each workspace can only be assigned once per agent"
    })
});

export const ZAgentWorkspaces = {
  /** GET /api/agents/:agentId/workspaces — list this agent's current assignments. */
  List: {
    method: "GET" as const,
    path: "/api/agents/:agentId/workspaces",
    PathVars: AgentWorkspacePathVarsSchema,
    Response: ApiSuccessSchema(AssignmentsResponseSchema)
  },

  /**
   * PUT /api/agents/:agentId/workspaces — replace the full assignment
   * list. Empty `assignments` clears all rows. Atomic: delete-all +
   * create-many run inside a single Prisma transaction.
   */
  Set: {
    method: "PUT" as const,
    path: "/api/agents/:agentId/workspaces",
    PathVars: AgentWorkspacePathVarsSchema,
    Body: SetAgentWorkspacesBodySchema,
    Response: ApiSuccessSchema(AssignmentsResponseSchema)
  }
} as const;
